import { Dispatch, SetStateAction, useState } from 'react';

export const LOCAL_STORAGE_PREFIX = 'guest';

type UseLocalStorageReturn<S> = [
    S,
    Dispatch<SetStateAction<S>>,
    () => void,
    () => void
];

export function useLocalStorage<S>(
    scope: string,
    key: string,
    initialValue: S | (() => S)
): UseLocalStorageReturn<S> {
    const storageKey = `${LOCAL_STORAGE_PREFIX}:${scope}:${key}`;

    const resolveInitialValue = (): S =>
        initialValue instanceof Function ? initialValue() : initialValue;

    const [storedValue, setStoredValue] = useState<S>(() => {
        if (typeof window === 'undefined') {
            return resolveInitialValue();
        }

        try {
            const item = window.localStorage.getItem(storageKey);
            return item ? JSON.parse(item) as S : resolveInitialValue();
        } catch (error) {
            console.log(error);
            return resolveInitialValue();
        }
    });

    const setValue: Dispatch<SetStateAction<S>> = (value) => {
        try {
            const valueToStore =
                value instanceof Function ? value(storedValue) : value;

            setStoredValue(valueToStore);

            if (typeof window !== 'undefined') {
                window.localStorage.setItem(storageKey, JSON.stringify(valueToStore));
            }
        } catch (error) {
            console.log(error);
        }
    };

    const removeValue = () => {
        try {
            setStoredValue(resolveInitialValue());

            if (typeof window !== 'undefined') {
                window.localStorage.removeItem(storageKey);
            }
        } catch (error) {
            console.log(error);
        }
    };

    const removeAllInScope = () => {
        try {
            if (typeof window === 'undefined') {
                return;
            }

            const scopePrefix = `${LOCAL_STORAGE_PREFIX}:`;
            const keysToRemove: string[] = [];

            for (let i = 0; i < window.localStorage.length; i++) {
                const key = window.localStorage.key(i);

                if (key?.startsWith(scopePrefix)) {
                    keysToRemove.push(key);
                }
            }

            keysToRemove.forEach((key) => window.localStorage.removeItem(key));
            setStoredValue(resolveInitialValue());
        } catch (error) {
            console.log(error);
        }
    };

    return [storedValue, setValue, removeValue, removeAllInScope];
}